import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { biometricsSupported, enableBiometrics } from "@/lib/webauthn";
import { Loader2, Fingerprint, ShieldCheck } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [step, setStep] = useState("form"); // form | biometric
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed || !signature.trim()) {
      setError("Please read and accept the Member Agreement and type your full legal name as your signature.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(name, email, password, ref, agreed, signature.trim());
      toast.success("Account created");
      if (biometricsSupported()) {
        setStep("biometric");
      } else {
        navigate("/subscribe");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const setupBiometrics = async () => {
    setBioLoading(true);
    try {
      await enableBiometrics(user || { email, name });
      toast.success("Biometric login enabled");
      navigate("/subscribe");
    } catch (err) {
      toast.error(err.message || "Could not enable biometrics. You can set it up later.");
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        {step === "form" ? (
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
            <h1 className="font-serif text-4xl mb-2">Enroll now</h1>
            <p className="text-sm text-muted-foreground mb-8">Free access to the full study archive.</p>

            {ref && <div className="mb-4 text-sm text-primary bg-primary/10 border border-primary/30 px-3 py-2" data-testid="referral-banner">You were referred by a friend — they'll get a free month when you subscribe.</div>}

            {error && <div className="mb-4 text-sm text-destructive-foreground bg-destructive/30 border border-destructive px-3 py-2" data-testid="register-error">{error}</div>}

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Full name</label>
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Email</label>
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-6 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <div className="border border-border bg-card p-4 mb-5" data-testid="member-agreement">
              <div className="text-xs uppercase tracking-widest font-mono text-primary mb-2">Member Agreement</div>
              <div className="text-xs text-muted-foreground leading-relaxed max-h-32 overflow-y-auto pr-1 mb-3">
                I understand that all content on <Wordmark /> is provided <strong>strictly for educational
                purposes only</strong> and that <strong>none of it shall be construed as legal advice</strong>. I agree
                to <strong>indemnify and hold harmless</strong> <Wordmark />, its owners, instructors, and
                affiliates from any and all claims, liabilities, or damages arising from my use of this material. I
                declare, <strong>under penalty of perjury</strong> under the laws of the United States, that the
                information I provide is true and correct, that I am of legal age, and that I take full and sole
                responsibility for any actions I choose to take. My electronic signature below has the same legal force
                and effect as a handwritten signature.
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input type="checkbox" data-testid="agreement-checkbox" checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[var(--primary)]" />
                <span>I have read, understand, and accept the Member Agreement above.</span>
              </label>
            </div>

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">E-signature — type your full legal name</label>
            <input data-testid="register-signature" value={signature} onChange={(e) => setSignature(e.target.value)}
              placeholder="e.g. John Michael Doe"
              className="w-full bg-muted border border-input px-3 py-2.5 mb-6 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors font-serif italic text-lg" />

            <button data-testid="register-submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign &amp; create account
            </button>

            <p className="mt-6 text-sm text-muted-foreground">Already enrolled? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
          </form>
        ) : (
          <div className="w-full max-w-sm text-center" data-testid="biometric-setup">
            <Fingerprint className="w-14 h-14 text-primary mx-auto mb-6" />
            <h1 className="font-serif text-4xl mb-2">Enable biometric login</h1>
            <p className="text-sm text-muted-foreground mb-8">
              Now that you've signed the agreement, add your device's fingerprint or face unlock for faster,
              password-free sign-in next time.
            </p>
            <button data-testid="biometric-enable" onClick={setupBiometrics} disabled={bioLoading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60 mb-3">
              {bioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Enable biometrics
            </button>
            <button data-testid="biometric-skip" onClick={() => navigate("/subscribe")}
              className="w-full text-sm text-muted-foreground hover:text-primary transition-colors py-2">
              Skip for now
            </button>
          </div>
        )}
      </div>

      <div className="hidden lg:block relative grain order-1 lg:order-2">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/8815849/pexels-photo-8815849.jpeg?auto=compress&cs=tinysrgb&w=1400')" }} />
        <div className="absolute inset-0 bg-background/80" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5 self-end"><Wordmark className="font-serif text-xl" /><Logo className="w-7 h-7" /></Link>
          <blockquote className="font-serif text-3xl leading-snug max-w-md ml-auto text-right">"Free from servitude — becoming the Secured Party Creditor."</blockquote>
        </div>
      </div>
    </div>
  );
}
